import {
  Award,
  BookOpen,
  ChevronRight,
  GraduationCap,
  Mail,
  Phone,
  User,
  Users,
} from 'lucide-react';
import { GOOD_ATTENDANCE_PERCENT } from '@/constants/attendance';
import { gradeFor } from '@/constants/grading';
import { formatInstituteDate } from '@/features/teacher/utils';
import type { ParentInfo, StudentProfileDetails } from './profileData';

interface ProfilePersonalInfoTabProps {
  details: StudentProfileDetails;
  /** Whose record this is, when the reader is not the child themselves. */
  childName?: string;
  onOpenParentTab: () => void;
  onOpenAcademicTab: () => void;
}

/** One row of the record. `null` is shown as a dash, never as a blank. */
function Row({
  label,
  value,
  mono = false,
  last = false,
}: {
  label: string;
  value: string | null;
  mono?: boolean;
  last?: boolean;
}) {
  return (
    <div
      className={
        last
          ? 'flex items-start py-1'
          : 'flex items-start border-b border-slate-50 dark:border-white/5 py-1'
      }
    >
      <span className="w-28 sm:w-44 text-slate-600 dark:text-slate-400 font-medium shrink-0">
        {label}
      </span>
      <span className="w-4 sm:w-6 text-center text-slate-400 dark:text-slate-500 font-normal shrink-0">
        :
      </span>
      <span
        className={
          mono
            ? 'flex-1 font-bold text-slate-700 dark:text-white font-mono text-left break-words'
            : 'flex-1 font-bold text-slate-700 dark:text-white text-left break-words'
        }
      >
        {value ?? '\u2014'}
      </span>
    </div>
  );
}

/**
 * Whether the record bears out praise: marks in a top grade band and
 * attendance at or above the bar. False while either is still unknown — a
 * child with nothing marked yet has not earned "doing great", and neither has
 * one whose marks are good but who is rarely in school.
 */
function isDoingWell(details: StudentProfileDetails): boolean {
  const band = details.overallPerformance
    ? gradeFor(details.overallPerformance.averageScore, 100)
    : null;
  const attendance = details.attendance.percentage;

  return (
    band?.tone === 'success' && attendance !== null && attendance >= GOOD_ATTENDANCE_PERCENT
  );
}

/**
 * A parent or guardian, as the summary card on this tab shows them — the four
 * facts the row prints, so a guardian contact can be drawn by the same row.
 */
function ParentRow({
  parent,
  initial,
  tone,
}: {
  parent: Pick<ParentInfo, 'name' | 'relation' | 'phone' | 'email'>;
  initial: string;
  tone: string;
}) {
  return (
    <div className="px-3.5 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/70 dark:border-slate-700/70 flex items-start gap-3.5">
      <div
        className={`h-10 w-10 rounded-full ${tone} text-white font-bold flex items-center justify-center shrink-0 shadow-xs`}
      >
        {initial}
      </div>
      <div className="flex flex-col min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-base font-bold text-slate-800 dark:text-white">{parent.name}</span>
          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-800 dark:bg-blue-900/60 dark:text-blue-200 border border-blue-200 dark:border-blue-800">
            {parent.relation}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600 dark:text-slate-300 mt-1 font-medium">
          <span className="flex items-center gap-1 font-mono break-all">
            <Phone className="h-3 w-3 text-slate-400 shrink-0" /> {parent.phone ?? '\u2014'}
          </span>
          <span className="flex items-center gap-1 break-all">
            <Mail className="h-3 w-3 text-slate-400 shrink-0" /> {parent.email ?? '\u2014'}
          </span>
        </div>
      </div>
    </div>
  );
}

export function ProfilePersonalInfoTab({
  details,
  onOpenParentTab,
  onOpenAcademicTab,
  childName,
}: ProfilePersonalInfoTabProps) {
  // A child rarely has an address of their own, so on their own panel the
  // family's stands in, named for whose it is. The parent's panel keeps the
  // child's alone: the family's addresses are on the card beside it.
  const familyEmail =
    !childName && !details.studentEmail
      ? [details.father, details.mother, ...details.emergencyContacts].find((person) => person?.email) ?? null
      : null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-5  animate-fade-in">
      {/* LEFT COLUMN: Personal Info & Preferences Cards (6 cols) */}
      <div className="lg:col-span-6 flex flex-col gap-5 ">
        {/* 1. Personal Information Card */}
        <div className="rounded-2xl bg-white dark:bg-[#1b1d34] border border-slate-200/80 dark:border-white/10 p-4  shadow-xs flex flex-col gap-2">
          <div className="flex items-center justify-between border-b border-slate-100 dark:border-white/10 pb-3.5">
            <div className="flex items-center gap-2.5">
              <div className="p-2 rounded-xl bg-indigo-50 text-indigo-600 dark:bg-indigo-950/60 dark:text-indigo-400 border border-indigo-200/70 dark:border-indigo-800/60">
                <User className="h-4.5 w-4.5" />
              </div>
              <h3 className="text-sm sm:text-base font-bold text-slate-900 dark:text-white">
                Personal Information
              </h3>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-1.5 text-xs sm:text-base">
            <Row label="Full Name" value={details.fullName} />
            <Row label="Class" value={details.className} />
            <Row label="Roll Number" value={details.rollNumber} mono />
           <Row label="House" value={details.house} last />
            <Row
              label="Date of Birth"
              value={details.dateOfBirth ? formatInstituteDate(details.dateOfBirth) : null}
            />
            <Row label="Age" value={details.age} />
            <Row label="Gender" value={details.gender} />
            <Row label="Blood Group" value={details.bloodGroup} />
            <Row label="Address" value={details.address} />
            <Row label="Phone (Student)" value={details.studentPhone}  />
            <Row
              label={familyEmail ? `Email (${familyEmail.relation})` : 'Email (Student)'}
              value={familyEmail?.email ?? details.studentEmail}
            />
           
          </div>
        </div>
      </div>

      {/* RIGHT COLUMN: Parent Summary, Academic Summary, & Banner (6 cols) */}
      <div className="lg:col-span-6 flex flex-col gap-5 ">
        {/* 1. Parent / Guardian Information Card */}
        <div className="rounded-2xl bg-white dark:bg-[#1b1d34] border border-slate-200/80 dark:border-white/10 p-4  shadow-xs flex flex-col gap-3">
          <div className="flex items-center justify-between border-b border-slate-100 dark:border-white/10 pb-3.5">
            <div className="flex items-center gap-2.5">
              <div className="p-2 rounded-xl bg-blue-50 text-blue-600 dark:bg-blue-950/60 dark:text-blue-400 border border-blue-200/70 dark:border-blue-800/60">
                <Users className="h-4.5 w-4.5" />
              </div>
              <h3 className="text-sm sm:text-base font-bold text-slate-900 dark:text-white">
                Parent / Guardian Information
              </h3>
            </div>
            <button
              type="button"
              onClick={onOpenParentTab}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-blue-50 text-blue-600 dark:bg-blue-950/60 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/80 border border-blue-200 dark:border-blue-800 text-sm font-bold transition-colors cursor-pointer"
            >
              <span>View all</span>
              <ChevronRight className="h-3 w-3" />
            </button>
          </div>

          <div className="flex flex-col gap-4">
            {details.father && <ParentRow parent={details.father} initial="F" tone="bg-blue-500" />}
            {details.mother && (
              <ParentRow parent={details.mother} initial="M" tone="bg-purple-500" />
            )}
            {/* A child in a guardian's care has no father or mother row, and
                "nobody recorded" would be untrue — the guardian is shown instead. */}
            {!details.father &&
              !details.mother &&
              details.emergencyContacts.map((guardian) => (
                <ParentRow key={guardian.id} parent={guardian} initial="G" tone="bg-emerald-500" />
              ))}
            {!details.father && !details.mother && details.emergencyContacts.length === 0 && (
              <p className="py-4 text-center text-sm font-medium text-slate-500 dark:text-slate-400">
                Your school office has not recorded a parent or guardian yet.
              </p>
            )}
          </div>
        </div>

        {/* 2. Academic Details Card */}
        <div className="rounded-2xl bg-white dark:bg-[#1b1d34] border border-slate-200/80 dark:border-white/10 p-4 shadow-xs flex flex-col gap-2">
          <div className="flex items-center justify-between border-b border-slate-100 dark:border-white/10 pb-3.5">
            <div className="flex items-center gap-2.5">
              <div className="p-2 rounded-xl bg-emerald-50 text-emerald-600 dark:bg-emerald-950/60 dark:text-emerald-400 border border-emerald-200/70 dark:border-emerald-800/60">
                <BookOpen className="h-4.5 w-4.5" />
              </div>
              <h3 className="text-sm sm:text-base font-bold text-slate-900 dark:text-white">
                Academic Details
              </h3>
            </div>
            <button
              type="button"
              onClick={onOpenAcademicTab}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-emerald-50 text-emerald-600 dark:bg-emerald-950/60 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/80 border border-emerald-200 dark:border-emerald-800 text-sm font-bold transition-colors cursor-pointer"
            >
              <span>View all</span>
              <ChevronRight className="h-3 w-3" />
            </button>
          </div>

          <div className="grid grid-cols-1 gap-1 text-xs sm:text-base">
            <Row label="Academic Year" value={details.academicYear} mono />
            <Row label="Class" value={details.className.replace('Class ', '')} />
            <Row label="Section" value={details.section} />
            <Row label="Roll Number" value={details.rollNumber} mono />
            <Row label="Admission Number" value={details.admissionNumber} mono last />
          </div>
        </div>

        {/* 3. Landscape "Keep Learning, Keep Growing!" Banner */}
        <div className="rounded-2xl bg-gradient-to-r from-blue-900/90 via-indigo-900/90 to-purple-900/90 text-white p-5 sm:p-6 shadow-xs flex items-center justify-between gap-4 relative overflow-hidden border border-indigo-700/40">
          <div className="flex items-center gap-4 relative z-10">
            <div className="h-12 w-12 rounded-xl bg-amber-400/20 border border-amber-300/30 flex items-center justify-center text-amber-300 shrink-0 shadow-xs">
              <Award className="h-7 w-7" />
            </div>
            <div className="flex flex-col">
              <h4 className="text-base sm:text-lg font-black text-white leading-tight">
                Keep Learning, Keep Growing!
              </h4>
              {isDoingWell(details) && (
                <p className="text-xs sm:text-sm text-indigo-100 font-medium mt-0.5">
                  {childName
                    ? `${details.fullName.split(' ')[0]} is doing great!`
                    : `You're doing great, ${details.fullName.split(' ')[0]}!`}
                </p>
              )}
            </div>
          </div>
          <GraduationCap className="h-20 w-20 text-white/10 absolute right-4 -bottom-3 pointer-events-none" />
        </div>
      </div>
    </div>
  );
}
